import { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronDownIcon, SearchIcon } from '@heroicons/react/outline';
import classNames from 'classnames';
import { useLanguages } from '../hooks/useUIConstants';

interface LanguageSelectorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder = 'Select a language...',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { handPickedLanguages, allLanguages, loading, error } =
    useLanguages(searchQuery);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  const getCurrentLanguage = () => {
    // If we have a value, return it directly - it doesn't matter which list it came from
    if (value) {
      return value;
    }
    return '';
  };

  const displayLanguages = useMemo(() => {
    return searchQuery ? allLanguages : handPickedLanguages;
  }, [searchQuery, allLanguages, handPickedLanguages]);

  const currentLanguage = getCurrentLanguage();

  if (error) {
    console.error('Error loading languages:', error);
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected language display */}
      <div
        className={classNames(
          'flex items-center justify-between rounded border-2 border-transparent bg-neutral-50 py-3 px-4 transition-colors',
          'text-neutral-150 focus:border-secondary-50 focus:outline-none focus:ring-0',
          'disabled:bg-transparent disabled:text-caption disabled:font-medium disabled:text-green-150',
          !disabled &&
            'cursor-pointer hover:border-secondary-50 hover:bg-neutral-75',
        )}
        onClick={() => !disabled && setIsExpanded(!isExpanded)}
      >
        <div className="flex flex-col">
          <span className="text-body text-neutral-150">
            {currentLanguage || placeholder}
          </span>
        </div>
        {!disabled && (
          <ChevronDownIcon
            className={classNames(
              'h-4 w-4 text-neutral-125 transition-transform',
              isExpanded && 'rotate-180',
            )}
          />
        )}
      </div>

      {/* Expanded language list */}
      {isExpanded && !disabled && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-neutral-75 bg-neutral-50 shadow-400">
          {/* Search field */}
          <div className="border-b border-neutral-50 bg-neutral-50 p-4">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-125" />
              <input
                type="text"
                placeholder="Search languages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded border border-neutral-75 bg-white py-2 pl-10 pr-3 text-small-caption shadow-sm focus:border-secondary-50 focus:outline-none"
              />
            </div>
          </div>

          {/* Language list */}
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-neutral-125">
                Loading languages...
              </div>
            ) : displayLanguages.length > 0 ? (
              <div className="space-y-1 p-2">
                {/* Show section header only when not searching and we have hand-picked languages */}
                {!searchQuery && handPickedLanguages.length > 0 && (
                  <div className="px-3 py-2">
                    <h4 className="font-sans text-small-caption text-neutral-125">
                      Popular Languages
                    </h4>
                  </div>
                )}
                {displayLanguages.map((language) => (
                  <button
                    key={language}
                    onClick={() => {
                      onChange(language);
                      setIsExpanded(false);
                    }}
                    className={classNames(
                      'flex w-full items-center justify-between rounded px-3 py-2 text-left text-small transition-colors',
                      'hover:bg-neutral-50',
                      value === language && 'bg-secondary-25 text-green-150',
                    )}
                  >
                    <span className="text-body text-neutral-150">
                      {language}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center text-neutral-125">
                No languages found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
